import { useQuery } from '@tanstack/react-query';
import { apiFetchWithRetry } from '@/lib/apiFetch';

interface TopicResponse {
  id: string;
  name: string;
  sortOrder?: number | null;
}

/** A topic option shaped for MultiSelectChips ({ code, label }). */
export interface TopicOption {
  code: string; // topic UUID
  label: string; // topic name
}

async function fetchSubjectTopics(subjectId: string): Promise<TopicOption[]> {
  const res = await apiFetchWithRetry(`/api/v1/catalog/subjects/${subjectId}/topics`);
  if (!res.ok) throw new Error(`Subject topics fetch failed: ${subjectId} (${res.status})`);
  const topics: TopicResponse[] = await res.json();
  return topics.map(t => ({ code: t.id, label: t.name }));
}

/**
 * Topics configured for a subject (public catalog). Returns the subject's fixed
 * topic list as { code: topicId, label: name } options for the topics filter.
 * Disabled until a subject is resolved, so it never fires with an empty id.
 */
export function useSubjectTopics(subjectId: string | undefined | null) {
  const query = useQuery<TopicOption[]>({
    queryKey: ['subject-topics', subjectId],
    queryFn: () => fetchSubjectTopics(subjectId as string),
    enabled: !!subjectId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    topics: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
